import React, { useEffect, useMemo, useRef, useState } from "react";
import { useEditorStore, useEditorStoreApi } from "../store/editorStoreSelectors";
import { usePointerDrag } from "../hooks/usePointerDrag";
import { useUndoGesture } from "../hooks/useUndoGesture";
import {
  cellOutline,
  findGutters,
  knifeSegmentInCell,
  mergeCells,
  moveGutter,
  resolveKnifeLine,
  splitCell,
} from "../utils/panelGeometry";
import { CSS_DPI } from "../constants/pageDimensions";
import { trackEvent } from "../utils/analytics";

const NON_PHOTO_BLUE = "rgba(164, 221, 237, 0.6)";
const STAMP_RED = "#b42318";
// Gutters narrower than this still get a grabbable strip this wide
const MIN_GRAB = 10;
// Screen px within which a dragged gutter lines up with a parallel one
const ALIGN_SNAP_PX = 6;

const inches = (px) => `${(px / CSS_DPI).toFixed(2)}″`;

const pointInPolygon = (x, y, points) => {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const [xi, yi] = points[i];
    const [xj, yj] = points[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};

/**
 * Direct manipulation of the page layout, drawn over the panels:
 *
 * - Knife: hold ⌥ (or turn on knife mode) and drag across a panel to split
 *   it — straight or at any angle. Shift forces a straight cut; Esc cancels.
 * - Gutters: drag any shared gutter to resize the panels on both sides at
 *   once; double-click it to merge the two panels it separates.
 */
const LayoutTools = ({ pageRef, pageWidth, pageHeight }) => {
  const storeApi = useEditorStoreApi();
  const cells = useEditorStore((st) => st.cells);
  const gutterSize = useEditorStore((st) => st.gutterSize);
  const zoom = useEditorStore((st) => st.zoom);
  const knifeMode = useEditorStore((st) => st.knifeMode);

  const [altHeld, setAltHeld] = useState(false);
  const [knife, setKnife] = useState(null); // { cellId, start, end, straight }
  const [refused, setRefused] = useState(null); // flash for a cut/merge that can't happen
  const [hoverGutter, setHoverGutter] = useState(null);
  const knifeRef = useRef(null);
  knifeRef.current = knife;

  const gutters = useMemo(() => findGutters(cells, gutterSize), [cells, gutterSize]);

  // ─── Page coordinates ───────────────────────────────────────────────────
  const toPage = (event) => {
    const page = pageRef.current;
    const rect = page.getBoundingClientRect();
    const z = storeApi.getState().zoom || 1;
    return {
      x: (event.clientX - rect.left) / z - page.clientLeft,
      y: (event.clientY - rect.top) / z - page.clientTop,
    };
  };

  const flashRefusal = (shape) => {
    setRefused(shape);
    window.setTimeout(() => setRefused(null), 450);
  };

  // ─── Knife ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleKey = (event) => setAltHeld(event.altKey);
    const handleBlur = () => setAltHeld(false);
    window.addEventListener("keydown", handleKey);
    window.addEventListener("keyup", handleKey);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleKey);
      window.removeEventListener("keyup", handleKey);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  // Crosshair over panels while the knife is ready
  useEffect(() => {
    const page = pageRef.current;
    if (!page) return;
    page.classList.toggle("striply-knife-ready", altHeld || knifeMode);
    page.classList.toggle("striply-knife-mode", knifeMode);
  }, [altHeld, knifeMode, pageRef]);

  useEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;

    const handlePointerDown = (event) => {
      const { knifeMode: modeOn } = storeApi.getState();
      if (!(event.altKey || modeOn)) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      // ⌥-click on a bubble/SFX still duplicates it; only knife mode cuts over items
      if (!modeOn && event.target.closest?.("[data-canvas-item]")) return;
      if (event.target.closest?.("[data-gutter-handle]")) return;

      const point = toPage(event);
      const target = [...storeApi.getState().cells].reverse()
        .find((c) => pointInPolygon(point.x, point.y, cellOutline(c)));
      if (!target) return;

      event.preventDefault();
      event.stopPropagation();
      setKnife({ cellId: target.id, start: point, end: point, straight: event.shiftKey });

      const handleMove = (moveEvent) => {
        if (moveEvent.pointerId !== event.pointerId) return;
        const end = toPage(moveEvent);
        setKnife((k) => (k ? { ...k, end, straight: moveEvent.shiftKey } : k));
      };
      const finish = (upEvent, cancelled) => {
        if (upEvent && upEvent.pointerId !== event.pointerId) return;
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", handleUp);
        window.removeEventListener("pointercancel", handleCancel);
        window.removeEventListener("keydown", handleEscape);
        const current = knifeRef.current;
        setKnife(null);
        if (cancelled || !current) return;
        const end = upEvent ? toPage(upEvent) : current.end;
        const line = resolveKnifeLine(current.start, end, { straight: upEvent?.shiftKey || current.straight });
        if (!line) return;
        const state = storeApi.getState();
        const cell = state.cells.find((c) => c.id === current.cellId);
        const halves = cell && splitCell(cell, line.p0, line.p1, state.gutterSize);
        if (!halves) {
          const segment = cell && knifeSegmentInCell(cell, line.p0, line.p1);
          flashRefusal({ type: "line", from: segment?.[0] || line.p0, to: segment?.[1] || line.p1 });
          return;
        }
        state.applyCellUpdate((prev) => prev.flatMap((c) => (c.id === cell.id ? halves : [c])));
        state.clearSelection();
        trackEvent("panel_cut", { orientation: line.orientation });
      };
      const handleUp = (upEvent) => finish(upEvent, false);
      const handleCancel = (cancelEvent) => finish(cancelEvent, true);
      const handleEscape = (keyEvent) => {
        if (keyEvent.key === "Escape") finish(null, true);
      };
      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", handleUp);
      window.addEventListener("pointercancel", handleCancel);
      window.addEventListener("keydown", handleEscape);
    };

    // Capture phase: the knife gets first refusal before panels and items
    page.addEventListener("pointerdown", handlePointerDown, true);
    return () => page.removeEventListener("pointerdown", handlePointerDown, true);
    // toPage reads refs and store state only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageRef, storeApi]);

  // ─── Gutters ────────────────────────────────────────────────────────────
  const gutterDrag = usePointerDrag({
    onMove: ({ dx, dy, event, payload }) => {
      const { run, startCells, alignTo } = payload;
      let delta = run.axis === "x" ? dx : dy;
      // Line up with a parallel gutter when close enough
      const target = run.at + delta;
      const near = alignTo.find((at) => Math.abs(at - target) * zoom <= ALIGN_SNAP_PX);
      if (near != null) delta = near - run.at;
      // The inches readout follows the pointer along the gutter
      const point = toPage(event);
      payload.crossAt = run.axis === "x" ? point.y : point.x;
      storeApi.getState().setCells(moveGutter(startCells, run, delta));
    },
  });
  useUndoGesture(Boolean(gutterDrag.active));

  const startGutterDrag = (run, event) => {
    if (event.altKey || storeApi.getState().knifeMode) return;
    event.preventDefault();
    event.stopPropagation();
    const point = toPage(event);
    gutterDrag.start(event, {
      run,
      startCells: storeApi.getState().cells,
      alignTo: gutters.filter((g) => g.axis === run.axis && g.id !== run.id).map((g) => g.at),
      crossAt: run.axis === "x" ? point.y : point.x,
    });
  };

  const mergeAcross = (run, event) => {
    event.preventDefault();
    event.stopPropagation();
    const point = toPage(event);
    const cross = run.axis === "x" ? point.y : point.x;
    const pair = run.pairs.find((p) => cross >= p.start - gutterSize && cross <= p.end + gutterSize) || run.pairs[0];
    const state = storeApi.getState();
    const merged = mergeCells(state.cells, pair.a, pair.b, state.gutterSize);
    if (!merged) {
      flashRefusal({ type: "gutter", runId: run.id });
      return;
    }
    state.applyCellUpdate(() => merged);
    state.setSelection({ type: "cell", cellId: pair.a });
    trackEvent("panels_merged", { via: "gutter" });
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  const draggingRun = gutterDrag.active?.run || null;
  // While dragging, draw the live gutter (its panels have moved)
  const liveRun = draggingRun
    ? gutters.find((g) => g.axis === draggingRun.axis
      && draggingRun.before.every((id) => g.before.includes(id))) || null
    : null;

  const knifePreview = (() => {
    if (!knife) return null;
    const line = resolveKnifeLine(knife.start, knife.end, { straight: knife.straight });
    if (!line) return null;
    const cell = cells.find((c) => c.id === knife.cellId);
    const segment = cell && knifeSegmentInCell(cell, line.p0, line.p1);
    if (!segment) return null;
    const [a, b] = segment;
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const ox = (-(b.y - a.y) / len) * (gutterSize / 2);
    const oy = ((b.x - a.x) / len) * (gutterSize / 2);
    return { a, b, band: `${a.x + ox},${a.y + oy} ${b.x + ox},${b.y + oy} ${b.x - ox},${b.y - oy} ${a.x - ox},${a.y - oy}` };
  })();

  const measurement = (() => {
    if (!draggingRun || !liveRun) return null;
    const size = liveRun.axis === "x" ? "width" : "height";
    const cross = gutterDrag.active.crossAt;
    const pair = liveRun.pairs.find((p) => cross >= p.start - gutterSize && cross <= p.end + gutterSize) || liveRun.pairs[0];
    const before = cells.find((c) => c.id === pair.a);
    const after = cells.find((c) => c.id === pair.b);
    if (!before || !after) return null;
    return { run: liveRun, cross, before: inches(before[size]), after: inches(after[size]) };
  })();

  // Keep the readout the same size on screen at any zoom, centred on its point
  const counterScale = { transform: `scale(${1 / (zoom || 1)}) translate(-50%, -50%)` };

  return (
    <>
      {gutters.map((run) => {
        const grab = Math.max(gutterSize, MIN_GRAB / (zoom || 1));
        const offset = (grab - gutterSize) / 2;
        const isActive = (liveRun && liveRun.id === run.id) || hoverGutter === run.id;
        const isRefused = refused?.type === "gutter" && refused.runId === run.id;
        const style = run.axis === "x"
          ? { left: run.at - offset, top: run.start, width: grab, height: run.end - run.start, cursor: "col-resize" }
          : { left: run.start, top: run.at - offset, width: run.end - run.start, height: grab, cursor: "row-resize" };
        return (
          <div
            key={run.id}
            data-gutter-handle="1"
            title="Drag to resize both sides · double-click to merge"
            onPointerEnter={() => setHoverGutter(run.id)}
            onPointerLeave={() => setHoverGutter((h) => (h === run.id ? null : h))}
            onPointerDown={(e) => startGutterDrag(run, e)}
            onDoubleClick={(e) => mergeAcross(run, e)}
            style={{
              position: "absolute",
              ...style,
              zIndex: 55,
              touchAction: "none",
              background: isRefused ? "rgba(180, 35, 24, 0.35)" : isActive ? NON_PHOTO_BLUE : "transparent",
              transition: "background-color 140ms ease",
            }}
          />
        );
      })}

      {measurement && (
        <div
          aria-live="polite"
          style={{
            position: "absolute",
            zIndex: 65,
            pointerEvents: "none",
            ...(measurement.run.axis === "x"
              ? { left: measurement.run.at + gutterSize / 2, top: measurement.cross }
              : { left: measurement.cross, top: measurement.run.at + gutterSize / 2 }),
          }}
        >
          <div
            style={{
              ...counterScale,
              transformOrigin: "0 0",
              display: "flex",
              gap: 6,
              alignItems: "center",
              whiteSpace: "nowrap",
              padding: "3px 7px",
              background: "#fffdf6",
              border: "1px solid #1a1a1a",
              boxShadow: "2px 2px 0 rgba(0,0,0,0.2)",
              fontFamily: "'Georgia', serif",
              fontSize: 11,
              color: "#1a1a1a",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            <span>{measurement.before}</span>
            <span style={{ color: "#00a4e4" }}>{measurement.run.axis === "x" ? "│" : "─"}</span>
            <span>{measurement.after}</span>
          </div>
        </div>
      )}

      {(knifePreview || refused?.type === "line") && (
        <svg
          aria-hidden="true"
          width={pageWidth}
          height={pageHeight}
          style={{ position: "absolute", left: 0, top: 0, zIndex: 66, pointerEvents: "none", overflow: "visible" }}
        >
          {knifePreview && (
            <>
              <polygon points={knifePreview.band} fill={NON_PHOTO_BLUE} />
              <line
                x1={knifePreview.a.x} y1={knifePreview.a.y} x2={knifePreview.b.x} y2={knifePreview.b.y}
                stroke="#1a1a1a" strokeWidth={1.5 / (zoom || 1)} strokeDasharray={`${6 / (zoom || 1)} ${4 / (zoom || 1)}`}
              />
            </>
          )}
          {refused?.type === "line" && (
            <line
              className="striply-guide"
              x1={refused.from.x} y1={refused.from.y} x2={refused.to.x} y2={refused.to.y}
              stroke={STAMP_RED} strokeWidth={2 / (zoom || 1)} strokeDasharray={`${6 / (zoom || 1)} ${4 / (zoom || 1)}`}
            />
          )}
        </svg>
      )}
    </>
  );
};

export default LayoutTools;
